import React, { createContext, useState, useEffect } from 'react';
import { api as collectionsAPI } from '@fiverr-private/collect';
import { getContext } from '@fiverr-private/fiverr_context';
import { logError } from '../../../../utils/logger/logError';
import type { Collection } from '../../../../types/collections';
import type { CollectionsContextProps, TCollectionsContext } from './types';

export const CollectionsContext = createContext<TCollectionsContext>({
  loaded: false,
});

export const CollectionsContextProvider = ({
  children,
  collections: collectionsFromProps,
  ...callbacks
}: CollectionsContextProps) => {
  const [fetchedCollections, setFetchedCollections] = useState<Collection[] | undefined>();

  const isLoggedInUser = Boolean(getContext().userId);

  const hasPassedCollectionsFromProps = collectionsFromProps !== undefined;
  const hasFetchedCollections = fetchedCollections !== undefined;

  const loaded = hasPassedCollectionsFromProps || hasFetchedCollections;

  const collections = hasPassedCollectionsFromProps ? collectionsFromProps : fetchedCollections;

  useEffect(() => {
    if (loaded) {
      return;
    }

    if (!isLoggedInUser) {
      setFetchedCollections([]); // don't fetch collections data if user is guest
      return;
    }

    let requestCancelled = false;

    const fetchCollections = async () => {
      let _collections: Collection[] = [];
      try {
        const { collections } = await collectionsAPI.getCollections();
        _collections = collections;
      } catch (error) {
        _collections = [];
        logError('CollectionsContext', 'fetchCollections: Failed to fetch collections from collectionsAPI', {
          error,
        });
      } finally {
        if (!requestCancelled) {
          setFetchedCollections(_collections);
        }
      }
    };

    fetchCollections();

    return () => {
      requestCancelled = true;
    };
  }, [loaded, isLoggedInUser]);

  return (
    <CollectionsContext.Provider
      value={{
        loaded,
        collections,
        ...callbacks,
      }}
    >
      {children}
    </CollectionsContext.Provider>
  );
};
